import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axiosClient from '../../libs/axiosClient';
import HeaderMain from '../../components/HeaderMain/HeaderMain';
import HeaderMobile from "../../components/HeaderMobile/HeaderMobile";
import Footer from '../../components/Footer/Footer';
import ChatModal from '../../components/ChatModal/ChatModal';
import PaymentModal from '../../components/PaymentModal/PaymentModal';
import styles from './TreatmentDetail.module.scss';
import detailStar from '../../assets/hospitalReservation/detailStar.png';

export interface Hospital {
  name: string;
  introduce: string;
  address_english: string;
  nearest_subway_station: string;
  operating_hours_notice: string;
  profile_image: string | null;
  tel: string;
  phone: string;
  email: string;
  postcode: string;
  address: string;
  address_add: string;
  status: string;
  id: number;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
}

export interface MedicalDepartment {
  title: string;
  description: string;
  is_use: boolean;
  sort_order: number;
  id: number;
  created_at: string;
  updated_at: string | null;
}

export interface TreatmentDetail {
  id: number;
  hospital_id: number;
  medical_department_id: number;
  medical_department_sub_id: number | null;
  title: string;
  description: string;
  price: number;
  is_vat_include: boolean;
  is_anesthetic_include: boolean;
  is_aftercare_include: boolean;
  procedure_time_minutes: number;
  recovery_time_minutes: number;
  status: string;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  hospital: Hospital;
  medical_department: MedicalDepartment;
  banner_image: string[];
  profile_image: string[];
}

export interface TreatmentOption {
  product_id: number;
  title: string;
  description: string;
  price: number;
  is_vat_include: boolean;
  is_anesthetic_include: boolean;
  is_aftercare_include: boolean;
  procedure_time_minutes: number;
  recovery_time_minutes: number;
  status: string;
  created_at: string;
  updated_at: string | null;
  deleted_at: string | null;
  id: number;
  currency_code: string;
  hospital_name: string;
}

export interface AdditionalService {
  title: string;
  description: string;
  is_use: boolean;
  is_confirm: boolean | null;
  sort_order: number;
  created_at: string;
  updated_at: string | null;
  id: number;
}

function TreatmentDetail() {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>(); 
  const [selectedTab, setSelectedTab] = useState('consultation');
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);
  const [isPaymentModalOpen, setIsPaymentModalOpen] = useState(false);
  const { t, i18n } = useTranslation();
  const [treatmentData, setTreatmentData] = useState<TreatmentDetail | null>(
    null
  );
  const [options, setOptions] = useState<TreatmentOption[]>([]);
  const [tags, setTags] = useState<AdditionalService[]>([]);
  const [rating, setRating] = useState<{ avg_score: number; total_review_quantity: number } | null>(null);
  const [isMobile, setIsMobile] = useState(false);
  const [consultCount, setConsultCount] = useState<number | null>(null);
  const [currentBannerIndex, setCurrentBannerIndex] = useState(0);
  
  useEffect(() => {
    const savedLanguage = localStorage.getItem('selectedLanguage') || 'en';
    
    if (i18n.language !== savedLanguage) {
      i18n.changeLanguage(savedLanguage);
    }
    
    const handleResize = () => {
      setIsMobile(window.innerWidth <= 768);
    };

    handleResize();
    window.addEventListener("resize", handleResize);
    
    const fetchTreatmentDetails = async () => {
      try {
        const response = await axiosClient.get(
          `/api/v1/product/${id}`
        );
        setTreatmentData(response.data);
        setOptions(response.data.options);
        setTags(response.data.hospital.additional_services || []);

        const ratingResponse = await axiosClient.get(
          `/api/v1/review/${response.data.hospital_id}/${response.data.id}/stats`
        );
        
        setRating({
          avg_score: ratingResponse.data.avg_score,
          total_review_quantity: ratingResponse.data.total_review_quantity,
        });
        
        const consultCountResponse = await axiosClient.get(
          `/api/v1/hospital/${response.data.hospital_id}/consult_count`
        );
        setConsultCount(consultCountResponse.data.count);
      } catch (error) {
        console.error('Error fetching treatment details:', error);
      }
    };

    fetchTreatmentDetails();
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, [i18n, id]);
  
  if (!treatmentData) {
    return <div>Loading...</div>;
  }
  
  const {
    title,
    price,
    is_vat_include,
    procedure_time_minutes,
    hospital,
    medical_department,
  } = treatmentData;
  
  const bannerImages = treatmentData?.banner_image?.length
    ? treatmentData.banner_image
    : ['https://via.placeholder.com/1920x500'];
  
  const handleNextBanner = () => {
    setCurrentBannerIndex((prevIndex) => (prevIndex + 1) % bannerImages.length);
  };

  const handlePrevBanner = () => {
    setCurrentBannerIndex((prevIndex) =>
      (prevIndex - 1 + bannerImages.length) % bannerImages.length
    );
  };

  return (
    <>
      {isChatModalOpen && 
        <ChatModal 
          onClose={() => setIsChatModalOpen(false)}
          hospitalId={treatmentData.hospital_id.toString()}
          hospitalName={treatmentData.hospital.name}
          profileImage={treatmentData.profile_image[0].toString() || ""}
        />
      }
        
      {isPaymentModalOpen && (
        <PaymentModal
          onClose={() => setIsPaymentModalOpen(false)}
          hospitalId={treatmentData.hospital_id.toString()}
          address={treatmentData.hospital.address_english}
          subway={treatmentData.hospital.nearest_subway_station}
          title={treatmentData.title}
          profileImage={treatmentData.profile_image[0].toString() || ""}
          options={options}
          hospitalName={treatmentData.hospital.name}
        />
      )}
      
      {isMobile ? (
        <HeaderMobile />
      ) : (
      <HeaderMain 
        selectedTab={selectedTab}
        onTabChange={setSelectedTab}
      />  
      )}
      <div className={styles.treatmentDetail}>
        <section className={styles.imageSection}>
          <div className={styles.bannerContainer}>
            <button className={styles.prevButton} onClick={handlePrevBanner}>
              &#8249;
            </button>
            <img
              src={bannerImages[currentBannerIndex]}
              alt={hospital?.name || 'Clinic'}
              className={styles.mainImage}
            />
            <button className={styles.nextButton} onClick={handleNextBanner}>
              &#8250;
            </button>
          </div>
          <div className={styles.treatmentInfo}>
            <p className={styles.hospitalAddress}>{hospital?.address_english} | {hospital?.nearest_subway_station}</p>
            <h1 className={styles.treatmentName}>{title}</h1>
            {/* <div className={styles.additionalInfo}>
              <img src={detailStar} alt="Rating" className={styles.icon} />
              {rating && (
                <span className={styles.ratingText}>
                  {rating.avg_score.toFixed(1)} ({rating.total_review_quantity})
                </span>
              )}
            </div> */}
            {/* <span className={styles.price}>
              {price}$
              <span className={styles.vatText}>{is_vat_include && 'VAT Included'}</span>
            </span> */}
          </div>
          
        </section>
        <div className={styles.thickDivider} />
        <section className={styles.section}>
          <h2>{t('treatmentDetail.clinicInfo.title')}</h2>
          <div className={styles.divider} />
          <p className={styles.subheading}>{t('treatmentDetail.clinicInfo.howToBook')}</p>
          <p className={styles.info}>{t('treatmentDetail.clinicInfo.chatWithClinic')}</p>
          <div className={styles.divider} />
          <p className={styles.subheading}>{t('treatmentDetail.clinicInfo.medicalSubject')}</p>
          <p className={styles.info}>{medical_department?.title}</p>
          <div className={styles.tagBox}>
            {tags.map((service, index) => (
              <span key={service.id}>
                {t(`treatmentDetail.clinicInfo.tags.${index}`, service.title)}
              </span>
            ))}
          </div>
          <div className={styles.divider} />
          <p className={styles.subheading}>
            {t('treatmentDetail.clinicInfo.additionalServices')}
          </p>
          <ul className={styles.serviceList}>
            {tags.map((service) => (
              <li key={service.id}>{service.title}</li>
            ))}
          </ul>
          <button
            className={styles.showMoreButton}
            onClick={() => navigate(`/clinic-detail/${hospital.id}`)}
          >
            {t('treatmentDetail.clinicInfo.moreInfo')}
          </button>
        </section>
        <div className={styles.thickDivider} />
        
        {/* 추후 이벤트 섹션 추가 예정 */}
        {/* <section className={styles.section}>
        <button
            className={styles.eventReviewButton}
            onClick={() =>
              navigate(`/event-review/${treatmentData.hospital_id}/${treatmentData.id}`)
            }
          >
            {t('treatmentDetail.clinicInfo.eventReviewButton')}
          </button>
        </section>
        <div className={styles.thickDivider} /> */}

        <section className={styles.section}>
          <h2>{t('treatmentDetail.treatmentOptions.title')}</h2>
            {options.map((option, index) => (
              <div key={option.id} className={styles.optionCard}>
                <div className={styles.leftSection}>
                  <div className={styles.optionHeader}>
                  {t('treatmentDetail.option')} {String(index + 1).padStart(2, '0')}.
                  </div>
                  <div className={styles.optionContent}>
                    <p className={styles.optionConetentHeader}>{option.description}</p>
                    {/* <p className={styles.optionConetentText}>
                      {t('treatmentDetail.procedureTime.title')} {option.procedure_time_minutes} minute
                      <br />
                      {t('treatmentDetail.recoveryTime.title')} {option.recovery_time_minutes} minute
                    </p> */}
                  </div>
                </div>
                <div className={styles.rightSection}>
                  {/*<span className={styles.additionalFees}>*/}
                  {/*{option.is_anesthetic_include*/}
                  {/*  ? t('treatmentDetail.anestheticFeesIncluded')*/}
                  {/*  : t('treatmentDetail.anestheticFeeNone')}*/}
                  {/*  <br />*/}
                  {/*  {option.is_aftercare_include*/}
                  {/*    ? t('treatmentDetail.aftercareFeeIncluded')*/}
                  {/*    : t('treatmentDetail.aftercareFeeNone')}*/}
                  {/*</span>*/}
                  <div className={styles.price}>
                    <span className={styles.vat}>
                      {option.is_vat_include ? t('treatmentDetail.vatIncluded') : ''}
                    </span>
                    <span>
                      {i18n.language === 'ja' 
                        ? new Intl.NumberFormat('ja-JP', {
                            style: 'currency',
                            currency: 'JPY',
                            maximumFractionDigits: 0,
                          }).format(Number(option.price) * 150)
                        : new Intl.NumberFormat('en-US', {
                            style: 'currency',
                            currency: option.currency_code,
                            maximumFractionDigits: 0,
                          }).format(Number(option.price))
                      }
                    </span>
                  </div>
                </div>
              </div>
            ))}
        </section>
        <div className={styles.thickDivider} />
        <section className={styles.section}>
          <h2 className={styles.durationHeader}>
            {t('treatmentDetail.procedureTime.title')} {procedure_time_minutes}
          </h2>
          <p className={styles.durationContent}>
          {t('treatmentDetail.procedureTime.note')} 
          </p>
        </section>
        <div className={styles.divider} />
        <section className={styles.section}>
          <h2>{t('treatmentDetail.additionalNotes.title')}</h2>
          <p className={styles.additionalNotes}>{t('treatmentDetail.additionalNotes.example')}</p>
        </section>
        <div className={styles.divider} />
        <section className={styles.section}>
          <h2>{t('treatmentDetail.hospitalDetails.title')}</h2>
          <p className={styles.address}>{hospital?.address}</p>
        </section>
        <div className={styles.divider} />
        <section className={styles.section}>
          <h2>{t('treatmentDetail.paymentNotice.title')}</h2>
          <p className={styles.additionalNotes}>{t('treatmentDetail.paymentNotice.description')}</p>
        </section>
        <div className={styles.divider} />
        {!isChatModalOpen && !isPaymentModalOpen && (
          <section className={styles.chatContainer}>
            <div className={styles.chatContent}>
            <p className={styles.chatText}>
              {consultCount === null ? (
                <p className={styles.chatText}>...</p>
              ) : (
                <p className={styles.chatText}>
                  {t('treatmentDetail.consultCount', { count: consultCount })}
                </p>
            )}
            </p>
              <div className={styles.chatButtons}>
                <button
                  className={styles.chatButton}
                  onClick={() => setIsChatModalOpen(true)}
                >
                  {t('treatmentDetail.clinicInfo.chatWithClinic')}
                </button>
                <button
                  className={styles.paymentButton}
                  onClick={() => setIsPaymentModalOpen(true)}
                >
                  {t('treatmentDetail.clinicInfo.makePayment')}
                </button>
              </div>
            </div>
          </section>
        )}
      </div>
      <Footer />
    </>
  );
}

export default TreatmentDetail;
